/**
 * A tiny read-only plan preview for the Sketches library, so a designer can
 * recognise a layout before opening it (owner request, 2026-07-25).
 *
 * It draws the same wall geometry the plan canvas does, but stripped to the
 * essentials — no labels, obstructions, thickness, pan/zoom — scaled to fit a
 * small fixed box. It reads a STORED document (walls with start/end in model
 * coordinates), not the live model, so the library can render every saved
 * sketch without loading it. Pure DOM: no model, no IndexedDB, so it is cheap
 * to call once per row.
 */

const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * Build an <svg> thumbnail of a stored sketch's plan.
 *
 * @param {object} doc  a stored sketch document (uses doc.walls)
 * @param {object} [opts]
 * @param {number} [opts.w] viewBox width  (px)
 * @param {number} [opts.h] viewBox height (px)
 * @param {number} [opts.pad] inner padding (px), kept clear of the frame
 * @returns {SVGSVGElement} an inline SVG element (empty-state if no geometry)
 */
export function planThumbSvg(doc, { w = 76, h = 52, pad = 6 } = {}) {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
  svg.setAttribute('class', 'sk-thumb');
  svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
  svg.setAttribute('aria-hidden', 'true'); // decorative; the row text names it

  const walls = Array.isArray(doc?.walls) ? doc.walls.filter((s) => s && s.start && s.end) : [];
  const pts = walls.flatMap((s) => [s.start, s.end]).filter((p) => p && Number.isFinite(p.x) && Number.isFinite(p.y));
  if (pts.length === 0) {
    // Empty sketch: a dash so the row still reads as "a sketch, just blank".
    const dash = document.createElementNS(SVG_NS, 'line');
    dash.setAttribute('x1', String(pad));
    dash.setAttribute('y1', String(h / 2));
    dash.setAttribute('x2', String(w - pad));
    dash.setAttribute('y2', String(h / 2));
    dash.setAttribute('class', 'sk-thumb__empty');
    svg.append(dash);
    return svg;
  }

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of pts) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }
  const bw = Math.max(maxX - minX, 1);
  const bh = Math.max(maxY - minY, 1);
  const scale = Math.min((w - pad * 2) / bw, (h - pad * 2) / bh);
  // Centre the scaled bbox in the box.
  const ox = (w - bw * scale) / 2 - minX * scale;
  const oy = (h - bh * scale) / 2 - minY * scale;
  const tx = (p) => ox + p.x * scale;
  const ty = (p) => oy + p.y * scale;

  for (const seg of walls) {
    const isOpening = (seg.segmentKind ?? 'wall') === 'opening';
    const line = document.createElementNS(SVG_NS, 'line');
    line.setAttribute('x1', tx(seg.start).toFixed(1));
    line.setAttribute('y1', ty(seg.start).toFixed(1));
    line.setAttribute('x2', tx(seg.end).toFixed(1));
    line.setAttribute('y2', ty(seg.end).toFixed(1));
    line.setAttribute('class', `sk-thumb__wall${isOpening ? ' sk-thumb__wall--opening' : ''}`);
    svg.append(line);
  }
  return svg;
}
